"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  Target,
  Award,
  Sparkles,
  CheckCircle,
} from "lucide-react";
import Link from "next/link";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAboutContacts,
  fetchClientStats,
  fetchGeneralSettings,
} from "@/store/slices/landingSlice";
import { getValidImageUrl } from "@/utils/imageUtils";
import companyLogo from "@/assets/logos/hubmicroo-logo-white.png";

const AboutPreviewSection = () => {
  const dispatch = useAppDispatch();
  const { data: aboutData } = useAppSelector(
    (state) => state.landing.aboutContact,
  );
  const { data: statsData } = useAppSelector(
    (state) => state.landing.clientStats,
  );
  const { data: settings } = useAppSelector(
    (state) => state.landing.generalSettings,
  );
  const { mode } = useAppSelector((state) => state.theme);
  const firstAbout = aboutData[0];

  const settingsData = settings as any;
  const whiteLogoUrl = companyLogo.src;

  React.useEffect(() => {
    dispatch(fetchAboutContacts());
    dispatch(fetchClientStats());
    if (!settings) dispatch(fetchGeneralSettings());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const stats = [
    {
      icon: Users,
      value: statsData[0]?.value || "100+",
      label: statsData[0]?.label || "Happy Clients",
    },
    {
      icon: Target,
      value: statsData[1]?.value || "100+",
      label: statsData[1]?.label || "Projects Done",
    },
    {
      icon: Award,
      value: statsData[2]?.value || "5+",
      label: statsData[2]?.label || "Awards Won",
    },
  ];

  return (
    <section
      className={`py-32 relative overflow-hidden transition-colors duration-700 ${mode === "dark" ? "bg-[#0B0F19]" : "bg-slate-50"}`}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${mode === "dark" ? "opacity-[0.05]" : "opacity-[0.03]"}`}
          style={{
            backgroundImage:
              mode === "dark"
                ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
                : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className={`absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] transition-opacity duration-700 ${mode === "dark" ? "bg-[#8B31FF]/10" : "bg-[#8B31FF]/5"}`}
        />
        <div
          className={`absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full blur-[100px] transition-opacity duration-700 ${mode === "dark" ? "bg-[#8B31FF]/5" : "bg-[#8B31FF]/3"}`}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Unified Badge Style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium mb-6 transition-all duration-300 ${
                mode === "dark"
                  ? "border-[#8B31FF]/30 bg-[#8B31FF]/10 text-[#8B31FF]"
                  : "border-purple-100 bg-purple-50 text-purple-700"
              }`}
            >
              <span className="relative flex h-2 w-2">
                <span
                  className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${mode === "dark" ? "bg-[#8B31FF]" : "bg-purple-400"}`}
                ></span>
                <span
                  className={`relative inline-flex rounded-full h-2 w-2 ${mode === "dark" ? "bg-[#8B31FF]" : "bg-purple-500"}`}
                ></span>
              </span>
              Who We Are
            </motion.div>

            <h2
              className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.15] ${mode === "dark" ? "text-white" : "text-gray-900"}`}
            >
              Crafting Digital <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] to-[#8B31FF]">
                Excellence & Innovation
              </span>
            </h2>

            <p
              className={`text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed max-w-2xl transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
            >
              {firstAbout?.subtitle ||
                "We are a team of passionate innovators, designers, and developers committed to transforming ideas into exceptional digital experiences. Our mission is to empower businesses with technology that drives growth."}
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`rounded-lg border px-5 py-4 transition-all duration-300 ${
                    mode === "dark"
                      ? "bg-white/[0.03] border-white/10"
                      : "bg-white/80 border-slate-200"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${mode === "dark" ? "bg-[#8B31FF]/15 text-[#8B31FF]" : "bg-[#8B31FF]/10 text-[#8B31FF]"}`}
                    >
                      <stat.icon className="w-4 h-4" />
                    </div>
                    <div
                      className={`text-2xl md:text-3xl font-bold leading-none transition-colors duration-300 ${mode === "dark" ? "text-white" : "text-gray-900"}`}
                    >
                      {stat.value}
                    </div>
                  </div>
                  <div
                    className={`text-sm font-medium transition-colors duration-300 ${mode === "dark" ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <Link href="/about-us">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`group inline-flex cursor-pointer items-center gap-2 rounded-lg px-8 py-4 text-base font-semibold transition-all shadow-lg hover:shadow-xl bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white hover:opacity-90`}
              >
                More About Us
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </motion.button>
            </Link>
          </motion.div>

          {/* Right Visual - Modern Composition */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Main Image Container */}
            <div
              className={`relative aspect-square rounded-[2rem] overflow-hidden shadow-2xl border transition-all duration-700 ${
                mode === "dark"
                  ? "bg-[#131620] border-white/5"
                  : "bg-gray-100 border-gray-100"
              }`}
            >
              {/* Abstract Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B31FF] via-[#6d28d9] to-[#8B31FF]" />
              <div className="absolute inset-0 opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

              {/* Center Floating Logo */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ perspective: 1200 }}
              >
                <motion.div
                  className="w-36 sm:w-44 md:w-52 lg:w-60 h-auto flex items-center justify-center"
                  animate={{
                    y: [0, -12, 0],
                    rotateY: [0, 10, -10, 0],
                  }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  {whiteLogoUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={whiteLogoUrl}
                      alt="Company Logo"
                      className="w-full h-auto object-contain filter drop-shadow-[0_20px_50px_rgba(255,255,255,0.15)] opacity-100"
                    />
                  ) : (
                    <div className="text-white text-3xl font-bold tracking-wider">
                      LOGO
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Floating Element 1 - Top Left */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute top-8 left-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
              >
                <Sparkles className="w-8 h-8 text-yellow-300" />
              </motion.div>

              {/* Floating Element 2 - Bottom Right */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute bottom-10 right-10 p-4 rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.12)] max-w-[160px] bg-white/10 backdrop-blur-md border border-white/20"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center bg-green-500/20 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <span className="font-bold text-sm text-white tracking-wide">
                    Success
                  </span>
                </div>
                <div className="h-2 w-full rounded-full overflow-hidden bg-white/20">
                  <div className="h-full w-[90%] bg-green-400 rounded-full shadow-[0_0_10px_rgba(74,222,128,0.5)]" />
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements behind */}
            <div className="absolute -z-10 -bottom-10 -left-10 w-full h-full border-2 border-[#8B31FF]/5 rounded-[2rem]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreviewSection;
